import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Clock, X, Plus, GripVertical, Search } from "lucide-react";
import { WORLD_CLOCK_CITIES, getCityById } from "./worldClockCities";

const STORAGE_POS_KEY = "crm_world_clock_pos";
const STORAGE_CITIES_KEY = "crm_world_clock_cities";

// ── Persistence helpers ──────────────────────────────────────────────────
function readPosition() {
    try {
        const raw = localStorage.getItem(STORAGE_POS_KEY);
        if (!raw) return null;
        const p = JSON.parse(raw);
        if (typeof p.x === "number" && typeof p.y === "number") return p;
        return null;
    } catch { return null; }
}

function writePosition(pos) {
    try { localStorage.setItem(STORAGE_POS_KEY, JSON.stringify(pos)); } catch { /* ignore */ }
}

function readCities() {
    try {
        const raw = localStorage.getItem(STORAGE_CITIES_KEY);
        if (!raw) return ["london", "newyork", "dubai", "tokyo"]; // defaults
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr : [];
    } catch { return ["london", "newyork", "dubai", "tokyo"]; }
}

function writeCities(ids) {
    try { localStorage.setItem(STORAGE_CITIES_KEY, JSON.stringify(ids)); } catch { /* ignore */ }
}

// ── Time helpers (uses Intl, no API needed) ──────────────────────────────
const timeFmtCache = {};
function getTimeFormatter(tz) {
    if (!timeFmtCache[tz]) {
        timeFmtCache[tz] = new Intl.DateTimeFormat(undefined, {
            timeZone: tz,
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false
        });
    }
    return timeFmtCache[tz];
}

const dayFmtCache = {};
function getDayFormatter(tz) {
    if (!dayFmtCache[tz]) {
        dayFmtCache[tz] = new Intl.DateTimeFormat(undefined, {
            timeZone: tz,
            weekday: "short",
            month: "short",
            day: "numeric"
        });
    }
    return dayFmtCache[tz];
}

// Day-of-week comparison vs. local: +1 / 0 / -1
function getDayOffsetLabel(tz, now) {
    try {
        const local = new Date(now);
        const fmt = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" });
        const tzDate = fmt.format(local); // YYYY-MM-DD
        const localFmt = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit" });
        const localDate = localFmt.format(local);
        if (tzDate === localDate) return null;
        return tzDate > localDate ? "+1 day" : "-1 day";
    } catch {
        return null;
    }
}

export function WorldClockWidget() {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(() => readPosition());
    const [cityIds, setCityIds] = useState(() => readCities());
    const [addOpen, setAddOpen] = useState(false);
    const [addSearch, setAddSearch] = useState("");
    const [now, setNow] = useState(() => new Date());

    // Drag state
    const dragRef = useRef(null);
    const buttonRef = useRef(null);
    const panelRef = useRef(null);
    const dragData = useRef({ dragging: false, offsetX: 0, offsetY: 0, moved: false });

    // Default position: top-right (24px from top, 24px from right)
    const effectivePos = pos || (() => {
        if (typeof window === "undefined") return { x: 24, y: 80 };
        return { x: Math.max(24, window.innerWidth - 64), y: 80 };
    })();

    // Tick every second
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    // Sync cities to storage
    useEffect(() => {
        writeCities(cityIds);
    }, [cityIds]);

    // ── Drag handlers ────────────────────────────────────────────────────
    const handlePointerDown = useCallback((e) => {
        // Only drag with left button or touch
        if (e.button !== undefined && e.button !== 0) return;
        const rect = buttonRef.current?.getBoundingClientRect();
        if (!rect) return;
        const clientX = e.clientX ?? e.touches?.[0]?.clientX;
        const clientY = e.clientY ?? e.touches?.[0]?.clientY;
        dragData.current = {
            dragging: true,
            offsetX: clientX - rect.left,
            offsetY: clientY - rect.top,
            moved: false
        };
        e.preventDefault();
    }, []);

    useEffect(() => {
        const handleMove = (e) => {
            if (!dragData.current.dragging) return;
            const clientX = e.clientX ?? e.touches?.[0]?.clientX;
            const clientY = e.clientY ?? e.touches?.[0]?.clientY;
            if (clientX === undefined || clientY === undefined) return;
            const newX = clientX - dragData.current.offsetX;
            const newY = clientY - dragData.current.offsetY;
            // Clamp to viewport
            const maxX = window.innerWidth - 48;
            const maxY = window.innerHeight - 48;
            const x = Math.max(8, Math.min(maxX, newX));
            const y = Math.max(8, Math.min(maxY, newY));
            // Mark as moved if drag distance exceeds tiny threshold
            const dx = Math.abs(x - effectivePos.x);
            const dy = Math.abs(y - effectivePos.y);
            if (dx > 3 || dy > 3) dragData.current.moved = true;
            setPos({ x, y });
        };
        const handleUp = () => {
            if (dragData.current.dragging) {
                if (dragData.current.moved) {
                    // Persist position if user actually dragged
                    writePosition(effectivePos);
                } else {
                    // Treat as click — toggle the panel
                    setOpen((v) => !v);
                }
            }
            dragData.current.dragging = false;
        };
        window.addEventListener("mousemove", handleMove);
        window.addEventListener("mouseup", handleUp);
        window.addEventListener("touchmove", handleMove);
        window.addEventListener("touchend", handleUp);
        return () => {
            window.removeEventListener("mousemove", handleMove);
            window.removeEventListener("mouseup", handleUp);
            window.removeEventListener("touchmove", handleMove);
            window.removeEventListener("touchend", handleUp);
        };
    }, [effectivePos]);

    // Click-outside close for panel
    useEffect(() => {
        if (!open) return;
        const onClick = (e) => {
            if (
                panelRef.current && !panelRef.current.contains(e.target) &&
                buttonRef.current && !buttonRef.current.contains(e.target)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    const cities = useMemo(() => cityIds.map(getCityById).filter(Boolean), [cityIds]);

    // Compute panel position so it stays on screen, anchored relative to button
    const panelPos = useMemo(() => {
        const panelWidth = 280;
        const panelHeight = Math.min(420, 80 + cities.length * 48 + 60);
        let left = effectivePos.x;
        let top = effectivePos.y + 56;
        // If overflowing right, anchor right edge to button
        if (left + panelWidth > window.innerWidth - 8) {
            left = Math.max(8, effectivePos.x + 48 - panelWidth);
        }
        // If overflowing bottom, show above the button
        if (top + panelHeight > window.innerHeight - 8) {
            top = Math.max(8, effectivePos.y - panelHeight - 8);
        }
        return { left, top };
    }, [effectivePos, cities.length]);

    const handleRemoveCity = (id) => {
        setCityIds((prev) => prev.filter((c) => c !== id));
    };

    const handleAddCity = (id) => {
        if (!cityIds.includes(id)) {
            setCityIds((prev) => [...prev, id]);
        }
        setAddOpen(false);
        setAddSearch("");
    };

    const filteredAddable = useMemo(() => {
        const q = addSearch.trim().toLowerCase();
        return WORLD_CLOCK_CITIES.filter((c) => !cityIds.includes(c.id))
            .filter((c) => {
                if (!q) return true;
                return c.city.toLowerCase().includes(q) || c.country.toLowerCase().includes(q);
            });
    }, [addSearch, cityIds]);

    return (
        <>
            {/* Floating draggable button */}
            <button
                ref={buttonRef}
                onMouseDown={handlePointerDown}
                onTouchStart={handlePointerDown}
                style={{
                    position: "fixed",
                    left: effectivePos.x,
                    top: effectivePos.y,
                    zIndex: 60,
                    touchAction: "none",
                    userSelect: "none",
                    cursor: dragData.current.dragging ? "grabbing" : "grab"
                }}
                className="h-12 w-12 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 text-white shadow-lg flex items-center justify-center transition-shadow border-2 border-white"
                title="World Clock — drag to move, click to open"
                aria-label="World clock"
            >
                <Clock className="h-5 w-5" />
            </button>

            {/* Panel */}
            {open && (
                <div
                    ref={panelRef}
                    style={{
                        position: "fixed",
                        left: panelPos.left,
                        top: panelPos.top,
                        zIndex: 60,
                        width: 280
                    }}
                    className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col"
                >
                    {/* Header */}
                    <div className="px-3 py-2.5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
                        <div className="flex items-center gap-1.5">
                            <Clock className="h-3.5 w-3.5 text-gray-500" />
                            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">World Clock</span>
                        </div>
                        <button
                            onClick={() => setOpen(false)}
                            className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"
                            aria-label="Close"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>

                    {/* City list */}
                    <div className="max-h-80 overflow-y-auto p-2">
                        {cities.length === 0 ? (
                            <div className="text-center py-6 text-xs text-gray-400">
                                No cities added.<br />Click <span className="font-medium text-gray-700">Add city</span> below.
                            </div>
                        ) : (
                            <ul className="space-y-1">
                                {cities.map((c) => {
                                    const time = getTimeFormatter(c.tz).format(now);
                                    const day = getDayFormatter(c.tz).format(now);
                                    const offsetLabel = getDayOffsetLabel(c.tz, now);
                                    return (
                                        <li
                                            key={c.id}
                                            className="group flex items-center justify-between gap-2 px-2 py-2 rounded-md hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                                <span className="text-base flex-shrink-0">{c.flag}</span>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-xs font-semibold text-gray-900 truncate">{c.city}</div>
                                                    <div className="text-[10px] text-gray-500 truncate">{day}{offsetLabel ? ` · ${offsetLabel}` : ""}</div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <span className="text-base font-mono font-semibold text-gray-900 tabular-nums">
                                                    {time}
                                                </span>
                                                <button
                                                    onClick={() => handleRemoveCity(c.id)}
                                                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-600"
                                                    aria-label={`Remove ${c.city}`}
                                                >
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    {/* Add city footer */}
                    <div className="border-t border-gray-100 bg-gray-50">
                        {!addOpen ? (
                            <button
                                onClick={() => setAddOpen(true)}
                                className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors"
                            >
                                <Plus className="h-3.5 w-3.5" />
                                Add city
                            </button>
                        ) : (
                            <div className="p-2">
                                <div className="relative mb-2">
                                    <Search className="h-3 w-3 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                                    <input
                                        autoFocus
                                        value={addSearch}
                                        onChange={(e) => setAddSearch(e.target.value)}
                                        placeholder="Search city or country..."
                                        className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div className="max-h-48 overflow-y-auto bg-white border border-gray-100 rounded-md">
                                    {filteredAddable.length === 0 ? (
                                        <div className="text-center py-4 text-xs text-gray-400">No matches</div>
                                    ) : (
                                        filteredAddable.map((c) => (
                                            <button
                                                key={c.id}
                                                onClick={() => handleAddCity(c.id)}
                                                className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs hover:bg-blue-50 border-b border-gray-50 last:border-0 text-left"
                                            >
                                                <span className="text-sm flex-shrink-0">{c.flag}</span>
                                                <span className="min-w-0 flex-1 truncate">
                                                    <span className="font-medium text-gray-900">{c.city}</span>
                                                    <span className="text-gray-400 ml-1">— {c.country}</span>
                                                </span>
                                            </button>
                                        ))
                                    )}
                                </div>
                                <button
                                    onClick={() => { setAddOpen(false); setAddSearch(""); }}
                                    className="w-full mt-1.5 text-[10px] text-gray-500 hover:text-gray-700"
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
